import { useState, useMemo, useEffect } from "react";
import { Search, MessageCircle, Mail, Phone, MapPin, ShieldCheck, ShieldX, Loader2, Filter, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Card, Badge, Button } from "@/components/kit";
import Pagination from "@/components/Pagination";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const STATUSES = ["new", "contacted", "replied", "negotiating", "closed", "lost"];

/**
 * Full-width lead picker with search / filter / pagination / big checkboxes.
 *
 * Props:
 *   leads, setLeads, selected, setSelected, channel ('wa' | 'email'), accent ('sky' | 'violet')
 */
export default function LeadPicker({ leads, setLeads, selected, setSelected, channel = "wa", accent = "sky" }) {
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [waOnly, setWaOnly] = useState(false);
  const [waChecking, setWaChecking] = useState(false);
  const [waAccount, setWaAccount] = useState(null);

  useEffect(() => {
    if (channel !== "wa") return;
    api.get("/wa/account").then((r) => setWaAccount(r.data)).catch(() => setWaAccount({ connected: false }));
  }, [channel]);

  const canValidate = !!(waAccount?.connected && waAccount?.device?.can_validate);
  const waBlockReason = !waAccount?.connected
    ? "Hubungkan akun Fonnte (WA Devices) dulu untuk Cek WA."
    : !waAccount?.device?.can_validate
      ? `Cek WA butuh paket Fonnte premium (Personal/Bisnis ke atas). Paket sekarang: ${waAccount?.device?.package || "—"}.`
      : "";

  // For email channel, the lead must have an email; for WA, a phone is required.
  const valid = useMemo(
    () => leads.filter((l) => (channel === "email" ? !!l.email : !!l.phone)),
    [leads, channel]
  );
  const cities = useMemo(() => Array.from(new Set(valid.map((l) => l.city).filter(Boolean))).sort(), [valid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return valid.filter((l) => {
      if (statusFilter && l.status !== statusFilter) return false;
      if (cityFilter && l.city !== cityFilter) return false;
      if (waOnly && channel === "wa" && l.wa_ready !== true) return false;
      if (!q) return true;
      return [l.business_name, l.phone, l.email, l.city, l.address, l.category]
        .some((v) => (v || "").toString().toLowerCase().includes(q));
    });
  }, [valid, search, statusFilter, cityFilter, waOnly, channel]);

  useEffect(() => { setPage(1); }, [search, statusFilter, cityFilter, waOnly, perPage]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const pageAllSelected = pageRows.length > 0 && pageRows.every((l) => selected[l.id]);

  const togglePage = (val) => {
    const next = { ...selected };
    pageRows.forEach((l) => { next[l.id] = val; });
    setSelected(next);
  };
  const toggleAllFiltered = () => {
    const next = { ...selected };
    filtered.forEach((l) => { next[l.id] = true; });
    setSelected(next);
  };
  const clearAll = () => setSelected({});

  const checkWaSelected = async () => {
    if (channel !== "wa") return;
    if (!canValidate) { toast.error(waBlockReason); return; }
    const ids = pageRows.filter((l) => selected[l.id] && l.wa_ready === undefined).map((l) => l.id);
    const target = ids.length > 0 ? ids : pageRows.filter((l) => l.wa_ready === undefined).map((l) => l.id);
    if (target.length === 0) { toast.info("Tidak ada lead di halaman ini yang perlu dicek"); return; }
    setWaChecking(true);
    try {
      const { data } = await api.post("/leads/check-wa", { numbers: target });
      const map = data.results || {};
      setLeads((rs) => rs.map((l) => {
        const v = map[l.id];
        if (v === true || v === false) return { ...l, wa_ready: v };
        return l;
      }));
      const ok = Object.values(map).filter((v) => v === true).length;
      const no = Object.values(map).filter((v) => v === false).length;
      toast.success(`${ok} terdaftar · ${no} tidak (dari ${target.length} dicek)`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setWaChecking(false); }
  };

  const accentClasses = accent === "violet"
    ? { ring: "ring-violet-400", bar: "bg-violet-500", soft: "bg-violet-50 text-violet-700", check: "accent-violet-500" }
    : { ring: "ring-sky-400", bar: "bg-sky-500", soft: "bg-sky-50 text-sky-700", check: "accent-sky-500" };

  return (
    <Card className="p-0 overflow-hidden" data-testid="lead-picker">
      {/* Toolbar */}
      <div className="p-4 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            data-testid="lead-picker-search"
            type="text"
            placeholder="Cari bisnis / telepon / email / kota…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-slate-200 pl-10 pr-3 py-2 text-sm focus:border-sky-300 outline-none"
          />
        </div>
        <select
          data-testid="lead-picker-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize bg-white"
        >
          <option value="">Semua Status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          data-testid="lead-picker-city"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white max-w-[180px]"
        >
          <option value="">Semua Kota</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {channel === "wa" && (
          <label className="flex items-center gap-2 text-sm text-slate-600 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
            <input type="checkbox" data-testid="lead-picker-wa-only" checked={waOnly} onChange={(e) => setWaOnly(e.target.checked)} />
            <span className="font-semibold text-emerald-700">WhatsApp Ready saja</span>
          </label>
        )}
      </div>

      {channel === "wa" && waAccount && !canValidate && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="lead-picker-wa-gate">
          <Lock className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            <b>Fitur Cek WA terkunci.</b> {waBlockReason}{" "}
            {waAccount.connected && (
              <a href="https://fonnte.com" target="_blank" rel="noreferrer" className="underline font-semibold">Upgrade Fonnte →</a>
            )}
          </span>
        </div>
      )}

      {/* Selection bar */}
      <div className="px-4 py-2.5 flex flex-wrap items-center gap-3 bg-slate-50 border-b border-slate-100 text-sm">
        <span className="text-slate-600">
          <b>{selectedIds.length}</b> dipilih · <b>{filtered.length}</b> tersedia
          <span className="text-slate-400"> · {valid.length} total</span>
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button data-testid="lead-picker-select-page" onClick={() => togglePage(!pageAllSelected)}
            className="text-xs font-semibold text-slate-700 hover:text-sky-600">
            {pageAllSelected ? "Batalkan halaman ini" : "Pilih halaman ini"}
          </button>
          <span className="text-slate-300">|</span>
          <button data-testid="lead-picker-select-all" onClick={toggleAllFiltered}
            className="text-xs font-semibold text-slate-700 hover:text-sky-600">
            Pilih semua ({filtered.length})
          </button>
          <span className="text-slate-300">|</span>
          <button data-testid="lead-picker-clear" onClick={clearAll}
            className="text-xs font-semibold text-rose-500 hover:text-rose-700">Bersihkan</button>
          {channel === "wa" && (
            <Button data-testid="lead-picker-check-wa" size="sm" variant="outline" loading={waChecking}
              onClick={checkWaSelected} disabled={!canValidate}
              title={canValidate ? "Cek WA untuk lead di halaman ini" : waBlockReason}>
              {canValidate ? <MessageCircle className="w-4 h-4 text-emerald-500" /> : <Lock className="w-4 h-4 text-slate-400" />}
              Cek WA
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/60 text-slate-500 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="p-3 w-12">
                <input
                  type="checkbox"
                  checked={pageAllSelected}
                  onChange={(e) => togglePage(e.target.checked)}
                  className={`w-5 h-5 ${accentClasses.check} cursor-pointer`}
                />
              </th>
              <th className="p-3">Bisnis</th>
              <th className="p-3 hidden md:table-cell">{channel === "email" ? "Email" : "Telepon"}</th>
              <th className="p-3 hidden lg:table-cell">Kota</th>
              <th className="p-3">Status</th>
              {channel === "wa" && <th className="p-3 text-center">WhatsApp</th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={channel === "wa" ? 6 : 5} className="p-12 text-center text-slate-400">
                Tidak ada lead cocok. {channel === "email" ? "Tambahkan email lewat scrape FB/IG atau lead manual." : "Scrape dulu di halaman Scraper."}
              </td></tr>
            ) : pageRows.map((l) => {
              const checked = !!selected[l.id];
              return (
                <tr key={l.id} data-testid="lead-picker-row"
                  onClick={() => setSelected((s) => ({ ...s, [l.id]: !s[l.id] }))}
                  className={`border-t border-slate-50 cursor-pointer transition ${checked ? `${accentClasses.soft.replace("text-", "ring-1 ring-")} ${accentClasses.soft}` : "hover:bg-slate-50/60"}`}>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setSelected((s) => ({ ...s, [l.id]: !s[l.id] }))}
                      className={`w-5 h-5 ${accentClasses.check} cursor-pointer`}
                    />
                  </td>
                  <td className="p-3">
                    <p className="font-semibold text-slate-800">{l.business_name}</p>
                    <p className="text-xs text-slate-400">{l.category || "—"}{l.rating ? ` · ⭐ ${l.rating}` : ""}</p>
                  </td>
                  <td className="p-3 hidden md:table-cell text-xs text-slate-600">
                    {channel === "email" ? (
                      <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-violet-500" /> <span className="truncate max-w-[220px] inline-block align-middle">{l.email}</span></span>
                    ) : (
                      <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-sky-500" /> {l.phone}</span>
                    )}
                  </td>
                  <td className="p-3 hidden lg:table-cell text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.city || "—"}</span>
                  </td>
                  <td className="p-3"><Badge color="slate">{l.status}</Badge></td>
                  {channel === "wa" && (
                    <td className="p-3 text-center">
                      {l.wa_ready === true ? (
                        <Badge color="green"><ShieldCheck className="w-3.5 h-3.5" /> Ready</Badge>
                      ) : l.wa_ready === false ? (
                        <Badge color="red"><ShieldX className="w-3.5 h-3.5" /> Tidak</Badge>
                      ) : (
                        <span className="text-slate-300 text-xs">Belum dicek</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer pagination */}
      <div className="p-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Per halaman:
          <select data-testid="lead-picker-per-page" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs">
            {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-slate-400">· halaman {page}/{totalPages}</span>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </Card>
  );
}
